import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  BookmarkIcon as BookmarkIconOutline,
  ClockIcon,
  LightBulbIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import VisionRelevanceWidget from './VisionRelevanceWidget';
import MarketTrendWidget from './MarketTrendWidget';
import HighlightableText from './HighlightableText';
import TaxImplicator from './TaxImplicator';
import ShareProgressButton from '../ShareProgressButton';
import './Learning.css';

const NodeContent = ({ userId }) => {
  const { categoryId, nodeId } = useParams();
  const [nodeData, setNodeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarked, setBookmarked] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [startTime] = useState(Date.now());
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [completedNodeData, setCompletedNodeData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadNode();
  }, [categoryId, nodeId]);

  const loadNode = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/learning/categories/${categoryId}/nodes/${nodeId}`);
      setNodeData(response.data);
    } catch (error) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!userId) {
      navigate(`/learning/${categoryId}`);
      return;
    }

    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      await axios.post('/api/learning/progress/complete', {
        user_id: userId,
        category_id: categoryId,
        node_id: nodeId,
        quiz_score: quizSubmitted ? quizScore : null,
        time_spent: timeSpent,
      });

      setJustCompleted(true);
      setCompletedNodeData({
        node_id: nodeId,
        node_title: nodeData?.title,
        category: categoryId,
      });
    } catch (error) {
      /* ignore */
    }
  };

  const handleBookmark = async () => {
    if (!userId) return;
    try {
      await axios.post('/api/learning/progress/bookmark', null, {
        params: { user_id: userId, category_id: categoryId, node_id: nodeId },
      });
      setBookmarked(!bookmarked);
    } catch (error) {
      /* ignore */
    }
  };

  const handleQuizSubmit = () => {
    if (!nodeData?.content?.quiz_questions) return;

    let correct = 0;
    nodeData.content.quiz_questions.forEach((question, index) => {
      if (quizAnswers[index] === question.correct_answer) correct++;
    });

    setQuizScore((correct / nodeData.content.quiz_questions.length) * 100);
    setQuizSubmitted(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading lesson…</p>
      </div>
    );
  }

  if (!nodeData) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <p className="text-body text-primary mb-4">Content not found</p>
          <button type="button" onClick={() => navigate(`/learning/${categoryId}`)} className="btn-secondary">
            Back to roadmap
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate(`/learning/${categoryId}`)}
          className="flex items-center gap-2 text-secondary hover:text-primary mb-8 transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="text-body">Back to roadmap</span>
        </button>

        <header className="flex items-start justify-between gap-4 mb-10 pb-8 border-b border-hairline">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl shrink-0">{nodeData.icon}</span>
              <div>
                <h1 className="font-display text-h1 text-primary mb-2">{nodeData.title}</h1>
                <div className="flex items-center gap-2 flex-wrap text-label text-secondary">
                  <span className="capitalize">{nodeData.level}</span>
                  <span className="text-faint">·</span>
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-4 h-4" />
                    {nodeData.duration}
                  </span>
                  <span className="text-faint">·</span>
                  <span className="capitalize">{nodeData.type}</span>
                </div>
              </div>
            </div>
            <p className="text-body text-secondary max-w-prose">{nodeData.description}</p>
          </div>

          <button
            type="button"
            onClick={handleBookmark}
            className={`icon-btn shrink-0 ${bookmarked ? 'icon-btn-active' : ''}`}
            aria-pressed={bookmarked}
          >
            {bookmarked ? (
              <BookmarkIconSolid className="w-5 h-5" />
            ) : (
              <BookmarkIconOutline className="w-5 h-5" />
            )}
          </button>
        </header>

        {userId && (
          <>
            <VisionRelevanceWidget categoryId={categoryId} nodeId={nodeId} userId={userId} />
            <MarketTrendWidget categoryId={categoryId} nodeId={nodeId} />
          </>
        )}

        <article className="mb-10">
          {nodeData.content.article_html && nodeData.content.type === 'article' && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() => {
                  if (!audioPlaying) {
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = nodeData.content.article_html;
                    const text = tempDiv.textContent || tempDiv.innerText || '';
                    if ('speechSynthesis' in window) {
                      const utterance = new SpeechSynthesisUtterance(text);
                      utterance.rate = 0.9;
                      utterance.onend = () => setAudioPlaying(false);
                      utterance.onerror = () => setAudioPlaying(false);
                      speechSynthesis.speak(utterance);
                      setAudioPlaying(true);
                    }
                  } else {
                    window.speechSynthesis.cancel();
                    setAudioPlaying(false);
                  }
                }}
                className="btn-ghost text-label flex items-center gap-2"
              >
                {audioPlaying ? (
                  <>
                    <SpeakerXMarkIcon className="w-4 h-4" />
                    Stop audio
                  </>
                ) : (
                  <>
                    <SpeakerWaveIcon className="w-4 h-4" />
                    Listen to article
                  </>
                )}
              </button>
            </div>
          )}

          {nodeData.content.type === 'article' && nodeData.content.article_html && (
            <div className="prose-editorial mx-auto">
              <HighlightableText
                content={nodeData.content.article_html}
                userId={userId}
                nodeId={nodeId}
                categoryId={categoryId}
              />
            </div>
          )}

          {nodeData.content.type === 'video' && (
            <div className="max-w-prose mx-auto">
              {nodeData.content.video_url && (
                <div className="video-frame mb-8">
                  <video src={nodeData.content.video_url} controls className="w-full h-full" />
                </div>
              )}
              {nodeData.content.article_html && (
                <div className="prose-editorial">
                  <HighlightableText
                    content={nodeData.content.article_html}
                    userId={userId}
                    nodeId={nodeId}
                    categoryId={categoryId}
                  />
                </div>
              )}
            </div>
          )}

          {nodeData.content.type === 'quiz' && (
            <div className="max-w-prose mx-auto">
              {nodeData.content.article_html && (
                <div
                  className="prose-editorial mb-10"
                  dangerouslySetInnerHTML={{ __html: nodeData.content.article_html }}
                />
              )}

              {nodeData.content.quiz_questions && (
                <div className="space-y-8">
                  <h2 className="font-display text-h2 text-primary">Test your knowledge</h2>
                  {nodeData.content.quiz_questions.map((question, qIndex) => (
                    <div key={qIndex} className="pb-8 border-b border-hairline last:border-0">
                      <p className="text-body font-medium text-primary mb-4">
                        {qIndex + 1}. {question.question}
                      </p>
                      <div className="space-y-2">
                        {question.options.map((option, oIndex) => {
                          const isSelected = quizAnswers[qIndex] === oIndex;
                          const isCorrect = oIndex === question.correct_answer;
                          const showAnswer = quizSubmitted;

                          let cls = 'quiz-option';
                          if (showAnswer && isCorrect) cls += ' quiz-option-correct';
                          else if (showAnswer && isSelected && !isCorrect) cls += ' quiz-option-incorrect';
                          else if (!showAnswer && isSelected) cls += ' quiz-option-selected';

                          return (
                            <button
                              key={oIndex}
                              type="button"
                              onClick={() =>
                                !quizSubmitted &&
                                setQuizAnswers({ ...quizAnswers, [qIndex]: oIndex })
                              }
                              disabled={quizSubmitted}
                              className={cls}
                            >
                              <span className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  readOnly
                                  checked={isSelected}
                                  className="radio-editorial pointer-events-none"
                                  tabIndex={-1}
                                />
                                {option}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {quizSubmitted && question.explanation && (
                        <p className="text-label text-secondary mt-4 p-3 bg-bone-raised border border-hairline rounded-md">
                          {question.explanation}
                        </p>
                      )}
                    </div>
                  ))}

                  {!quizSubmitted ? (
                    <button
                      type="button"
                      onClick={handleQuizSubmit}
                      disabled={
                        Object.keys(quizAnswers).length !== nodeData.content.quiz_questions.length
                      }
                      className="btn-primary w-full"
                    >
                      Submit quiz
                    </button>
                  ) : (
                    <div className="surface-panel p-6 text-center">
                      <p className="figure font-display text-h2 text-primary tabular-nums mb-2">
                        {quizScore.toFixed(0)}%
                      </p>
                      <p className="text-body text-secondary">
                        {quizScore >= 80
                          ? 'Excellent work.'
                          : quizScore >= 60
                          ? 'Good job — review anything unclear.'
                          : 'Keep learning — revisit the material when ready.'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {nodeData.content.type === 'simulation' && (
            <div className="max-w-prose mx-auto">
              {nodeData.content.article_html && (
                <div
                  className="prose-editorial mb-8"
                  dangerouslySetInnerHTML={{ __html: nodeData.content.article_html }}
                />
              )}
              <div className="surface-panel p-8 text-center border border-hairline">
                <p className="text-label text-secondary mb-2">Interactive simulation</p>
                <p className="text-body text-primary">
                  Type: {nodeData.content.simulation_type}
                </p>
              </div>
            </div>
          )}
        </article>

        {nodeData.content.key_takeaways?.length > 0 && (
          <section className="surface-panel p-6 mb-8 max-w-prose mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <LightBulbIcon className="w-5 h-5 text-pine" />
              <h2 className="font-display text-h2 text-primary">Key takeaways</h2>
            </div>
            <ul className="space-y-3">
              {nodeData.content.key_takeaways.map((takeaway, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-pine shrink-0 mt-0.5" />
                  <span className="text-body text-primary">{takeaway}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {nodeData.content.type === 'article' && (
          <TaxImplicator actionDescription={`Learning about: ${nodeData.title}`} userId={userId} />
        )}

        <div className="max-w-prose mx-auto">
          <button
            type="button"
            onClick={handleComplete}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <CheckCircleIcon className="w-5 h-5" />
            Mark as complete
          </button>

          {justCompleted && completedNodeData && userId && (
            <div className="mt-4">
              <ShareProgressButton
                entityType="learning"
                entityId={completedNodeData.node_id}
                entityData={completedNodeData}
                user={{ user_id: userId }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NodeContent;
